import React from 'react';
import { Helmet } from 'react-helmet-async';

interface Photo {
  s3Key: string;
  isPrimary: boolean;
}

interface ListingSEOProps {
  title: string;
  description: string;
  listingName: string;
  photos: Photo[];
  price?: number;
  address?: string;
  showAddress: string;
  s3BaseUrl: string;
}

const ListingSEO: React.FC<ListingSEOProps> = ({
  title,
  description,
  listingName,
  photos,
  price,
  address,
  showAddress,
  s3BaseUrl,
}) => {
  const siteUrl = window.location.origin;
  const pageUrl = `${siteUrl}/listing/${listingName}`;
  
  // Get primary photo or first photo
  const primaryPhoto = photos.find(p => p.isPrimary) || photos[0];
  const imageUrl = primaryPhoto ? `${s3BaseUrl}/${primaryPhoto.s3Key}` : '';
  
  // Truncate description for meta tags (max 160 chars for SEO)
  const metaDescription = description.length > 160 
    ? description.substring(0, 157) + '...' 
    : description;
  
  // Create a better page title with the site name
  const pageTitle = `${title} | Pay2Rent.me`;
  
  // Build structured data (JSON-LD) for search engines
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: title,
    description: description,
    image: photos.map(p => `${s3BaseUrl}/${p.s3Key}`),
    url: pageUrl,
    ...(price && {
      offers: {
        '@type': 'Offer',
        price: price,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: pageUrl,
      }
    }),
    ...((showAddress === 'public' || showAddress === 'captcha' || showAddress === 'always') && address && {
      location: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: address,
        }
      }
    }),
    brand: {
      '@type': 'Brand',
      name: 'Pay2Rent.me'
    },
    category: 'Rental',
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={pageUrl} />
      
      {/* Open Graph Tags for Facebook, LinkedIn, etc. */}
      <meta property="og:type" content="product" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="Pay2Rent.me" />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      {imageUrl && <meta property="og:image:secure_url" content={imageUrl} />}
      {imageUrl && <meta property="og:image:alt" content={title} />}
      {imageUrl && <meta property="og:image:width" content="1200" />}
      {imageUrl && <meta property="og:image:height" content="630" />}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      {imageUrl && <meta name="twitter:image:alt" content={title} />}
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default ListingSEO;

